import { useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { ResponsiveContainer } from 'recharts'
import { CHART_PRINT_ID } from 'src/Libs/dynamic-power-prices/config'
import { makeStyles } from 'tss-react/mui'
import { CostSavingsChartsProps } from '../../types'
import { BarChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { ErrorSkeleton } from '@e3dc-react/shell/Elements/Error'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import Legend from './Legend'
import Chart from './Chart'
import { useIsDynamicEmsAccessible } from '../../hooks'
import { useUserSettings } from 'src/Hooks'
import ProgressDetails from 'src/Libs/chart-kit/shared/components/ProgressDetails'
import useHideTraces from 'src/Libs/chart-kit/shared/hooks/use-hide-traces'
import useDynamicPricesSettings from '../../hooks/use-dynamic-prices-settings'
import { useTypedSelector } from 'src/State/RootReducer'
import ChartLoadingBackdrop from 'src/Elements/ChartLoadingBackdrop'
import { createPortal } from 'react-dom'
import { COST_SAVINGS_CHART_MAIN_WRAPPER_ID } from '../CostSavings'
import { FetchingState } from 'src/Libs/chart-kit'
import { Stack } from '@mui/system'
import { CostSavingChartTraceKey, CostSavingChartTraceKeys } from '../../cost-saving-chart-trace-keys'

export const HIDDEN_TRACE_KEYS_FOR_DAY_VIEW: CostSavingChartTraceKey[] = [
  CostSavingChartTraceKeys.ThreeSixtyBatterySavings,
  CostSavingChartTraceKeys.ThreeSixtyWallboxSavings,
]

const CHART_HEIGHT = 400

const useStyles = makeStyles()(theme => ({
  container: {
    ' .recharts-surface': {
      overflow: 'visible',
      ' .recharts-cartesian-grid .recharts-cartesian-grid-vertical line:last-of-type': {
        strokeOpacity: 0,
      },
      ' .recharts-cartesian-grid .recharts-cartesian-grid-vertical line:nth-last-of-type(2)': {
        strokeOpacity: 0,
      },
    },
  },
}))

/**
 * Electricity chart which displays the electricity consumption and prices.
 * Combines stacked bars and a line.
 */
const ChartContainer: React.FC<CostSavingsChartsProps> = ({
  dateSettings,
  data,
  loadingState,
  showLegend,
  chartEventHandlers,
  zoomStartIndex,
  zoomEndIndex,
  isZoomedIn,
  zoomedFrom,
  zoomedTo,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { view } = dateSettings

  const { isDynamicEmsAccessible, isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const isDataLoading = loadingState.state === FetchingState.IsFetching
  const isLegendVisible = !isDataLoading && (showLegend || !downSm)
  const isDataFetchedAndEmpty = !isDataLoading && data.length === 0
  const { hiddenTraces, toggleTraceVisibility } = useHideTraces<CostSavingChartTraceKey>()
  const hiddenTracesWithInitialValues = useMemo(
    () => (view === 'day' ? [...HIDDEN_TRACE_KEYS_FOR_DAY_VIEW, ...hiddenTraces] : hiddenTraces),
    [view, hiddenTraces]
  )
  const { userSettings } = useUserSettings()
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const { dynamicPricesSettings } = useDynamicPricesSettings({ storageId: storageId ?? '' })
  const showDynamicEmsLegend =
    isLegendVisible &&
    isDynamicEmsAccessible &&
    !isDynamicEmsV2Accessible &&
    userSettings.dynamicEmsEnabledOnce &&
    view === 'day' &&
    dynamicPricesSettings?.isDynamicEmsEnabledForBattery

  const renderContent = useCallback(() => {
    if (isDataLoading) {
      return (
        <>
          <BarChartSkeleton
            hideLoadingSpinner
            loading={isDataLoading}
            showLoadingPercent
            progressDetails={<ProgressDetails loadingState={loadingState} />}
          />
          {createPortal(
            <ChartLoadingBackdrop loadingState={loadingState} />,
            document.getElementById(COST_SAVINGS_CHART_MAIN_WRAPPER_ID) || document.body
          )}
        </>
      )
    }

    if (isDataFetchedAndEmpty) {
      return <ErrorSkeleton type={'lineChart'} title={t('common.noData')} />
    }

    return (
      <Chart
        dynamicPricesSettings={dynamicPricesSettings}
        data={data}
        dateSettings={dateSettings}
        hiddenTraces={hiddenTracesWithInitialValues}
        zoomStartIndex={zoomStartIndex}
        zoomEndIndex={zoomEndIndex}
        isZoomedIn={isZoomedIn}
        chartEventHandlers={chartEventHandlers}
        zoomedFrom={zoomedFrom}
        zoomedTo={zoomedTo}
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dateSettings, isDataFetchedAndEmpty, isDataLoading, loadingState, hiddenTracesWithInitialValues])

  return (
    <Stack id={CHART_PRINT_ID} sx={{ p: { xs: '0px 12px', md: 0 }, mb: 2 }} gap={2}>
      <ResponsiveContainer height={CHART_HEIGHT} width={'100%'} className={classes.container}>
        {renderContent()}
      </ResponsiveContainer>
      {isLegendVisible && (
        <Legend
          view={view}
          hiddenTraces={hiddenTracesWithInitialValues}
          toggleTraceVisibility={toggleTraceVisibility}
          showDynamicEmsLegend={showDynamicEmsLegend}
        />
      )}
    </Stack>
  )
}

export default ChartContainer
